import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";

import { ChartDataset, ChartOptions } from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import { capitalize } from "lodash";

import { useAppSelector } from "app/hooks";
import CircularProgress from "components/circular-progress";
import {
  AUDIOGRAM_PRINT_DEVICE_PIXEL_RATIO,
  HZ_VALUES,
  getVisibleFrequencies,
  snapToNearestFrequency
} from "constants/audiogram";
import {
  AUDIOGRAM_LOGARITHMIC_AXIS,
  CLICKING_ON_AUDIOGRAM_TO_COLLECT_MEASUREMENT
} from "constants/feature-flags";
import {
  buildHearingLossRanges,
  normalizeHearingLossThresholds
} from "constants/hearing-loss";
import {
  SPEECH_BANANA_FILL_COLOR,
  SPEECH_BANANA_FONT,
  SPEECH_BANANA_LINE_COLOR,
  SPEECH_BANANA_LINE_WIDTH,
  SPEECH_BANANA_PHONEMES,
  SPEECH_BANANA_TEXT_COLOR,
  SpeechBananaEdges,
  SpeechBananaPoint,
  buildSpeechBananaEdges,
  speechBananaFrequencyIndex
} from "constants/speech-banana";
import { selectCurrentPractice } from "features/auth/auth-slice";

import usePureToneImages from "../use-pure-tone-images.hook";
import { LEGEND_IMAGE } from "./pure-tone-audiogram-legend-item.component";
import { PureToneGraphItem } from "./pure-tone-grap-item.type";
import { formatGraphDataset } from "./utils";

type MenuState = {
  position: { x: number; y: number } | null;
  isMergedView: boolean;
  selectedEar: "left" | "right" | null;
  selectedFrequency: number | null;
  yAxisValue: number | null;
};

// Define the types of tests that can be selected
const TEST_TYPES = {
  AIR_CONDUCTION: "airConduction",
  AIR_CONDUCTION_MASKING: "airConductionMaskingLevel",
  BONE_CONDUCTION: "boneConduction",
  BONE_CONDUCTION_MASKING: "boneConductionMaskingLevel",
  MCL: "mcl",
  UCL: "ucl",
  SOUNDFIELD_AIDED: "soundfieldAided",
  SOUNDFIELD_UNAIDED: "soundfieldUnaided"
};

const TEST_TYPES_LABELS = {
  [TEST_TYPES.AIR_CONDUCTION]: "Air Conduction",
  [TEST_TYPES.AIR_CONDUCTION_MASKING]: "Air Conduction Masking",
  [TEST_TYPES.BONE_CONDUCTION]: "Bone Conduction",
  [TEST_TYPES.BONE_CONDUCTION_MASKING]: "Bone Conduction Masking",
  [TEST_TYPES.MCL]: "MCL",
  [TEST_TYPES.UCL]: "UCL",
  [TEST_TYPES.SOUNDFIELD_AIDED]: "Soundfield Aided",
  [TEST_TYPES.SOUNDFIELD_UNAIDED]: "Soundfield Unaided"
};

const LEGEND_IMAGE_MAPPING = {
  left: {
    [TEST_TYPES.AIR_CONDUCTION]: LEGEND_IMAGE.ACUL,
    [TEST_TYPES.AIR_CONDUCTION_MASKING]: LEGEND_IMAGE.ACML,
    [TEST_TYPES.BONE_CONDUCTION]: LEGEND_IMAGE.BCUL,
    [TEST_TYPES.BONE_CONDUCTION_MASKING]: LEGEND_IMAGE.BCML,
    [TEST_TYPES.MCL]: LEGEND_IMAGE.MCLL,
    [TEST_TYPES.UCL]: LEGEND_IMAGE.UCLL,
    [TEST_TYPES.SOUNDFIELD_AIDED]: LEGEND_IMAGE.AL,
    [TEST_TYPES.SOUNDFIELD_UNAIDED]: LEGEND_IMAGE.SL
  },
  right: {
    [TEST_TYPES.AIR_CONDUCTION]: LEGEND_IMAGE.ACUR,
    [TEST_TYPES.AIR_CONDUCTION_MASKING]: LEGEND_IMAGE.ACMR,
    [TEST_TYPES.BONE_CONDUCTION]: LEGEND_IMAGE.BCUR,
    [TEST_TYPES.BONE_CONDUCTION_MASKING]: LEGEND_IMAGE.BCMR,
    [TEST_TYPES.MCL]: LEGEND_IMAGE.MCLR,
    [TEST_TYPES.UCL]: LEGEND_IMAGE.UCLR,
    [TEST_TYPES.SOUNDFIELD_AIDED]: LEGEND_IMAGE.AR,
    [TEST_TYPES.SOUNDFIELD_UNAIDED]: LEGEND_IMAGE.SR
  }
};

const baseYScale = {
  reverse: true,
  offset: true,
  min: -10,
  max: 120,
  ticks: {
    autoSkip: false,
    stepSize: 10,
    padding: 10,
    color: "#000000"
  },
  grid: {
    offset: false,
    drawTicks: false,
    color: grey[400]
  },
  title: {
    text: "Hearing Level in dB",
    display: true,
    color: "#000000",
    font: {
      size: 14
    }
  }
};

const categoryXScale = {
  offset: false,
  ticks: {
    padding: 10,
    autoSkip: false,
    color: "#000000"
  },
  grid: {
    drawTicks: false,
    color: grey[400]
  },
  title: {
    text: "Frequency in Hz",
    display: true,
    color: "#000000",
    font: {
      size: 14
    }
  }
};

// Controls how x-axis tick labels are rendered on the logarithmic scale.
// - null / falsy: render each frequency as a single-row label, unchanged;
//   neighbouring labels may overlap at narrow widths.
// - "staggered": render labels across two rows (even-indexed on top, odd on
//   bottom) so close-together log-space pairs (1000/1500, 12000/16000) don't
//   collide. Chart.js renders `string[]` return values as one row per entry.
// - "short": collapse values >= 1000 to "NK" / "N.NK" (e.g. 1000 -> "1K",
//   1500 -> "1.5K", 16000 -> "16K") so the labels fit on a single row.
export type XLabelsPositionMode = "staggered" | "short" | null;

const formatShortFrequency = (hz: number): string =>
  hz >= 1000 ? `${hz / 1000}K` : String(hz);

const buildLogXTickCallback =
  (mode: XLabelsPositionMode, visibleFrequencies: number[]) =>
  (tickValue: string | number, index: number): string | string[] => {
    const n = Number(tickValue);
    if (!visibleFrequencies.includes(n)) return "";
    if (mode === "staggered") {
      return index % 2 === 0 ? [String(n), ""] : ["", String(n)];
    }
    if (mode === "short") {
      return formatShortFrequency(n);
    }
    return String(n);
  };

// ANSI S3.6 logarithmic x-axis. `afterBuildTicks` overrides Chart.js'
// default log-scale tick generator (which would emit 100/200/1000/...)
// so only the visible audiometric frequencies are rendered. The label renderer
// itself is controlled by `xLabelsPositionMode` -- see `buildLogXTickCallback`.
const buildLogarithmicXScale = (
  mode: XLabelsPositionMode,
  visibleFrequencies: number[]
) => ({
  type: "logarithmic" as const,
  min: 100,
  max: (visibleFrequencies.at(-1) ?? HZ_VALUES.at(-1)!) * 1.25,
  offset: false,
  ticks: {
    padding: 8,
    autoSkip: false,
    color: "#000000",
    crossAlign: "near" as const,
    font: {
      size: 12,
      lineHeight: 1.4
    },
    callback: buildLogXTickCallback(mode, visibleFrequencies)
  },
  afterBuildTicks: (axis: any) => {
    axis.ticks = visibleFrequencies.map((v) => ({ value: v }));
  },
  grid: {
    drawTicks: false,
    color: grey[400]
  },
  title: {
    text: "Frequency in Hz",
    display: true,
    color: "#000000",
    font: {
      size: 14
    }
  }
});

const baseOptions: ChartOptions = {
  responsive: true,
  animation: false,
  maintainAspectRatio: false,
  aspectRatio: 1,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: baseYScale,
    x: categoryXScale
  }
};

let _hearingLossOverlayEnabled = false;
let _hearingLossRanges = buildHearingLossRanges();

const hearingLossOverlayPlugin = {
  id: "hearingLossOverlay",
  beforeDraw: (chart: any) => {
    if (!_hearingLossOverlayEnabled) return;

    const {
      ctx,
      scales: { y },
      chartArea
    } = chart;

    _hearingLossRanges.forEach(({ min, max, color, label }) => {
      const yTop = y.getPixelForValue(min);
      const yBottom = y.getPixelForValue(max);
      const width = chartArea.right - chartArea.left;

      ctx.save();
      ctx.fillStyle = color;
      ctx.fillRect(chartArea.left, yTop, width, yBottom - yTop);
      ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
      ctx.font = "bold 14px sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(label, chartArea.right - 6, (yTop + yBottom) / 2);
      ctx.restore();
    });
  }
};

type SpeechBananaPluginOptions = {
  enabled?: boolean;
  useLogScale?: boolean;
};

// Trace a Catmull-Rom spline (expressed as cubic beziers) through `points`.
// Straight segments between the nine outline anchors would read as a polygon
// rather than a banana, but the curve must still pass exactly THROUGH each
// anchor -- an approximating curve pulls the edge inward and drops the labels
// nearest the ends outside the shaded region.
const traceSplineThrough = (
  ctx: CanvasRenderingContext2D,
  points: SpeechBananaPoint[]
) => {
  for (let i = 0; i < points.length - 1; i += 1) {
    const previous = points[i - 1] ?? points[i];
    const start = points[i];
    const end = points[i + 1];
    const following = points[i + 2] ?? points[i + 1];

    ctx.bezierCurveTo(
      start.x + (end.x - previous.x) / 6,
      start.y + (end.y - previous.y) / 6,
      end.x - (following.x - start.x) / 6,
      end.y - (following.y - start.y) / 6,
      end.x,
      end.y
    );
  }
};

// Across the quiet edge, straight down the 8000 Hz end, back along the loud
// edge, then closed up the 250 Hz end.
const traceSpeechBanana = (
  ctx: CanvasRenderingContext2D,
  { top, bottom }: SpeechBananaEdges
) => {
  ctx.beginPath();
  ctx.moveTo(top[0].x, top[0].y);
  traceSplineThrough(ctx, top);
  ctx.lineTo(bottom[0].x, bottom[0].y);
  traceSplineThrough(ctx, bottom);
  ctx.closePath();
};

// Per-chart config is read from `chart.options.plugins.speechBanana` rather
// than module-level state, so the split view's two charts and the hidden
// print chart can't overwrite each other's settings.
const speechBananaPlugin = {
  id: "speechBanana",
  beforeDatasetsDraw: (
    chart: any,
    _args: unknown,
    options: SpeechBananaPluginOptions
  ) => {
    if (!options?.enabled) return;

    const {
      ctx,
      scales: { x, y },
      chartArea
    } = chart;
    if (!x || !y || !chartArea) return;

    // The category axis is indexed by position in HZ_VALUES; the logarithmic
    // axis (AUDIOGRAM_LOGARITHMIC_AXIS) takes the frequency itself.
    const toX = (hz: number) =>
      x.getPixelForValue(
        options.useLogScale ? hz : speechBananaFrequencyIndex(hz)
      );
    const toY = (db: number) => y.getPixelForValue(db);

    ctx.save();
    // A practice can configure a narrower dB range than the banana spans, so
    // clip to the plot rather than letting it bleed over the axis labels.
    ctx.beginPath();
    ctx.rect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );
    ctx.clip();

    traceSpeechBanana(ctx, buildSpeechBananaEdges(toX, toY));
    ctx.fillStyle = SPEECH_BANANA_FILL_COLOR;
    ctx.fill();
    ctx.strokeStyle = SPEECH_BANANA_LINE_COLOR;
    ctx.lineWidth = SPEECH_BANANA_LINE_WIDTH;
    ctx.stroke();

    ctx.fillStyle = SPEECH_BANANA_TEXT_COLOR;
    ctx.font = SPEECH_BANANA_FONT;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    SPEECH_BANANA_PHONEMES.forEach(({ hz, db, label }) => {
      ctx.fillText(label, toX(hz), toY(db));
    });

    ctx.restore();
  }
};

interface PureToneGraphProps {
  label?: string;
  items: PureToneGraphItem[];
  sx?: SxProps;
  pdfIdentifier?: string;
  // Renders the canvas at the print/capture DPR (see isPrintVariant below).
  // Defaults to detecting print instances from `pdfIdentifier`; pass explicitly
  // to be unambiguous (mirrors TympanometryChart's `printView` prop).
  printView?: boolean;
  isMergedView?: boolean;
  showOverlay?: boolean;
  // Draws the speech banana -- the region of the audiogram where the phonemes
  // of conversational speech fall. Opt-in per report; never persisted.
  showSpeechBanana?: boolean;
  // Controls the rendering of x-axis tick labels when the logarithmic axis
  // is active. Omit to get the flag-driven default ("short" when the
  // AUDIOGRAM_LOGARITHMIC_AXIS flag is on, otherwise null). Passing `null`
  // explicitly disables stagger/short rendering even when the flag is on.
  xLabelsPositionMode?: XLabelsPositionMode;
  // When true, renders the "Copy to Clipboard" button above the chart
  // aligned to the right, instead of below it.
  copyButtonTop?: boolean;
  hideUltraHigh?: boolean;
  onDataUpdate?: (
    ear: "left" | "right",
    frequency: number,
    testType: string,
    value: number
  ) => void;
}

export default function PureToneGraph({
  items,
  label,
  sx,
  pdfIdentifier = "",
  printView,
  isMergedView = false,
  showOverlay = false,
  showSpeechBanana = false,
  xLabelsPositionMode,
  copyButtonTop = false,
  hideUltraHigh = false,
  onDataUpdate
}: PureToneGraphProps) {
  const flags = useFlags();
  const visibleFrequencies = useMemo(
    () => getVisibleFrequencies(hideUltraHigh),
    [hideUltraHigh]
  );
  const currentPractice = useAppSelector(selectCurrentPractice);
  const clickingOnAudiogramToCollectMeasurementsEnabled =
    flags[CLICKING_ON_AUDIOGRAM_TO_COLLECT_MEASUREMENT];
  const useLogScale = Boolean(flags[AUDIOGRAM_LOGARITHMIC_AXIS]);
  // Preserve an explicitly-passed `null` (caller opt-out) vs. an absent
  // prop (fall back to the flag-driven default: "short" when the log-axis
  // flag is on, otherwise null).
  const defaultLabelsMode: XLabelsPositionMode = useLogScale ? "short" : null;
  const resolvedLabelsMode: XLabelsPositionMode =
    xLabelsPositionMode === undefined ? defaultLabelsMode : xLabelsPositionMode;
  const ref = useRef<HTMLDivElement | null>(null);

  // Print/capture instances are rasterized by nodeToPng; render their canvas at
  // the capture DPR so it isn't upscaled and blurred (SN-18855). On-screen
  // instances keep the browser default DPR. Detection prefers the explicit
  // `printView` prop; the `pdfIdentifier` fallback keys off the same "-print" id
  // convention the nodeToPng capture selectors rely on (e.g.
  // getElementById("pure-tone-graph-canvas-print")), so the two stay in lockstep.
  const isPrintVariant = printView ?? pdfIdentifier.includes("print");

  const { imagesLoaded, pointStyles } = usePureToneImages();

  const hearingLossThresholds = useMemo(
    () =>
      normalizeHearingLossThresholds(currentPractice?.hearing_loss_thresholds),
    [currentPractice?.hearing_loss_thresholds]
  );
  const hearingLossRanges = useMemo(
    () => buildHearingLossRanges(hearingLossThresholds),
    [hearingLossThresholds]
  );

  _hearingLossOverlayEnabled = showOverlay;
  _hearingLossRanges = hearingLossRanges;

  const [pinnedEar, setPinnedEar] = useState<"left" | "right" | null>(
    isMergedView ? null : (label?.toLowerCase() as "left" | "right")
  );
  const [pinnedTestType, setPinnedTestType] = useState<string | null>(null);

  const [menuState, setMenuState] = useState<MenuState>({
    position: null,
    isMergedView,
    selectedEar: null,
    selectedFrequency: null,
    yAxisValue: null
  });

  // Close the menu
  const handleCloseMenu = useCallback(() => {
    setMenuState({
      ...menuState,
      position: null,
      selectedEar: null
    });
  }, [menuState]);

  // Handle test type selection and update data
  const handleTestTypeSelection = useCallback(
    (testType: string) => {
      if (
        onDataUpdate &&
        menuState.selectedFrequency !== null &&
        menuState.yAxisValue !== null
      ) {
        const ear =
          menuState.selectedEar ||
          (label?.toLowerCase() === "left" ? "left" : "right");
        onDataUpdate(
          ear,
          menuState.selectedFrequency,
          testType,
          menuState.yAxisValue
        );
      }

      // Close the menu
      handleCloseMenu();
    },
    [
      handleCloseMenu,
      label,
      menuState.selectedEar,
      menuState.selectedFrequency,
      menuState.yAxisValue,
      onDataUpdate
    ]
  );

  // Handle ear selection (for merged view)
  const handleEarSelection = useCallback(
    (ear: "left" | "right") => {
      setMenuState({
        ...menuState,
        selectedEar: ear
      });
      if (pinnedTestType) {
        handleTestTypeSelection(pinnedTestType);
      }
    },
    [handleTestTypeSelection, menuState, pinnedTestType]
  );

  // Determine if we're showing the ear selection menu or test type menu
  const showEarSelectionMenu = useMemo(
    () =>
      menuState.position !== null &&
      menuState.isMergedView &&
      menuState.selectedEar === null &&
      pinnedEar === null,
    [
      menuState.isMergedView,
      menuState.position,
      menuState.selectedEar,
      pinnedEar
    ]
  );
  const showTestTypeMenu = useMemo(
    () =>
      menuState.position !== null &&
      (!menuState.isMergedView || menuState.selectedEar !== null) &&
      pinnedTestType === null,
    [
      menuState.isMergedView,
      menuState.position,
      menuState.selectedEar,
      pinnedTestType
    ]
  );

  const onClickOptions = useMemo(() => {
    return clickingOnAudiogramToCollectMeasurementsEnabled
      ? {
          events: ["click"],
          onClick: (e: any, _elements: any, chart: any) => {
            const canvasPosition = getRelativePosition(e, chart as any);
            const boundingRect = (
              chart.canvas as HTMLCanvasElement
            ).getBoundingClientRect();

            const absoluteX = boundingRect.left + canvasPosition.x;
            const absoluteY = boundingRect.top + canvasPosition.y;

            let frequency: number;
            if (useLogScale) {
              const rawHz = chart.scales.x.getValueForPixel(
                canvasPosition.x
              ) as number | undefined;
              const snapped = snapToNearestFrequency(
                typeof rawHz === "number" ? rawHz : Number.NaN
              );
              if (snapped == null) return;
              frequency = snapped;
            } else {
              const dataX =
                chart.scales.x.getValueForPixel(canvasPosition.x) ?? -1;
              const frequencyIndex = Math.round(dataX);
              if (
                frequencyIndex < 0 ||
                frequencyIndex >= visibleFrequencies.length
              )
                return;
              frequency = visibleFrequencies[frequencyIndex];
            }

            if (!visibleFrequencies.includes(frequency)) return;

            const dataY =
              chart.scales.y.getValueForPixel(canvasPosition.y) ?? -1;
            const roundedDB = Math.round(dataY);

            if (pinnedEar && pinnedTestType && onDataUpdate) {
              onDataUpdate(pinnedEar, frequency, pinnedTestType, roundedDB);
              return;
            }

            // Set menu state
            setMenuState({
              position: {
                x: absoluteX,
                y: absoluteY
              },
              isMergedView,
              selectedEar: pinnedEar,
              selectedFrequency: frequency,
              yAxisValue: roundedDB
            });
          }
        }
      : {};
  }, [
    clickingOnAudiogramToCollectMeasurementsEnabled,
    useLogScale,
    isMergedView,
    onDataUpdate,
    pinnedEar,
    pinnedTestType,
    visibleFrequencies
  ]);

  useEffect(() => {
    const ear = label?.toLowerCase();
    if (!isMergedView && ear && ["left", "right"].includes(ear)) {
      handleEarSelection(ear as "left" | "right");
    }
  }, [label, isMergedView, handleEarSelection]);

  const chartOptions = useMemo<ChartOptions>(() => {
    const yScale = {
      ...baseYScale,
      min: hearingLossThresholds.floor,
      max: hearingLossThresholds.ceiling
    };
    const speechBanana: SpeechBananaPluginOptions = {
      enabled: showSpeechBanana,
      useLogScale
    };
    if (!useLogScale) {
      return {
        ...baseOptions,
        scales: {
          y: yScale,
          x: categoryXScale
        },
        plugins: {
          ...baseOptions.plugins,
          speechBanana
        }
      } as ChartOptions;
    }
    return {
      ...baseOptions,
      scales: {
        y: yScale,
        x: buildLogarithmicXScale(resolvedLabelsMode, visibleFrequencies)
      },
      plugins: {
        ...baseOptions.plugins,
        speechBanana,
        tooltip: {
          callbacks: {
            title: (items: any[]) =>
              items?.[0] ? `${items[0].parsed.x} Hz` : ""
          }
        }
      }
    } as ChartOptions;
  }, [
    useLogScale,
    resolvedLabelsMode,
    hearingLossThresholds,
    showSpeechBanana,
    visibleFrequencies
  ]);

  const copyButton = imagesLoaded ? (
    <Button
      variant="outlined"
      onClick={() =>
        ref.current?.querySelector("canvas")?.toBlob(function (blob) {
          const item = new ClipboardItem({ "image/png": blob as Blob });
          navigator.clipboard.write([item]);
        })
      }
      sx={{ display: "block" }}
      size="small"
    >
      Copy to Clipboard
    </Button>
  ) : null;

  return (
    <Stack gap={1}>
      {copyButtonTop && (
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mb: -5, pr: 2.5 }}
        >
          {copyButton}
        </Box>
      )}
      <Box
        ref={ref}
        id={`pure-tone-graph-canvas${pdfIdentifier}`}
        width={630}
        height={resolvedLabelsMode === "staggered" ? 540 : 520}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
          pr: 2,
          color: "#000000",
          ...sx
        }}
      >
        {imagesLoaded ? (
          <Chart
            type="line"
            plugins={[hearingLossOverlayPlugin, speechBananaPlugin]}
            options={{
              ...chartOptions,
              ...(onClickOptions as any),
              ...(isPrintVariant
                ? { devicePixelRatio: AUDIOGRAM_PRINT_DEVICE_PIXEL_RATIO }
                : {}),
              plugins: {
                ...chartOptions.plugins,
                title: {
                  display: true,
                  text: label,
                  font: {
                    size: 16
                  },
                  color: "#000000"
                }
              }
            }}
            data={{
              ...(useLogScale ? {} : { labels: visibleFrequencies }),
              datasets: formatGraphDataset(
                items,
                pointStyles,
                useLogScale,
                visibleFrequencies
              ) as ChartDataset[]
            }}
          />
        ) : (
          <CircularProgress />
        )}
      </Box>
      <Stack direction="row" spacing={1} justifyContent="center">
        {!copyButtonTop && copyButton}
        {clickingOnAudiogramToCollectMeasurementsEnabled && (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            {isMergedView && pinnedEar && (
              <Chip
                label={`Collected Ear: ${capitalize(pinnedEar)}`}
                onDelete={() => setPinnedEar(null)}
              />
            )}
            {pinnedTestType && (
              <Chip
                label={
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {`Collected Test: ${TEST_TYPES_LABELS[pinnedTestType]}`}
                    <Box
                      component="img"
                      src={
                        LEGEND_IMAGE_MAPPING[pinnedEar || "left"][
                          pinnedTestType
                        ]
                      }
                      sx={{ width: 32, ml: 1 }}
                    />
                  </Stack>
                }
                onDelete={() => setPinnedTestType(null)}
              />
            )}
          </Stack>
        )}
      </Stack>
      <Menu
        open={showEarSelectionMenu}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          menuState.position
            ? { top: menuState.position.y, left: menuState.position.x }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            setPinnedEar("left");
            handleEarSelection("left");
          }}
        >
          Left Ear
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPinnedEar("right");
            handleEarSelection("right");
          }}
        >
          Right Ear
        </MenuItem>
      </Menu>
      <Menu
        open={showTestTypeMenu}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          menuState.position
            ? { top: menuState.position.y, left: menuState.position.x }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            setPinnedTestType(TEST_TYPES.AIR_CONDUCTION);
            handleTestTypeSelection(TEST_TYPES.AIR_CONDUCTION);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={
                LEGEND_IMAGE_MAPPING[menuState.selectedEar || "left"][
                  TEST_TYPES.AIR_CONDUCTION
                ]
              }
              sx={{ width: 32, mb: 1 }}
            />
          </ListItemIcon>
          {TEST_TYPES_LABELS[TEST_TYPES.AIR_CONDUCTION]}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPinnedTestType(TEST_TYPES.AIR_CONDUCTION_MASKING);
            handleTestTypeSelection(TEST_TYPES.AIR_CONDUCTION_MASKING);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={
                LEGEND_IMAGE_MAPPING[menuState.selectedEar || "left"][
                  TEST_TYPES.AIR_CONDUCTION_MASKING
                ]
              }
              sx={{ width: 32, mb: 1 }}
            />
          </ListItemIcon>
          {TEST_TYPES_LABELS[TEST_TYPES.AIR_CONDUCTION_MASKING]}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPinnedTestType(TEST_TYPES.BONE_CONDUCTION);
            handleTestTypeSelection(TEST_TYPES.BONE_CONDUCTION);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={
                LEGEND_IMAGE_MAPPING[menuState.selectedEar || "left"][
                  TEST_TYPES.BONE_CONDUCTION
                ]
              }
              sx={{ width: 32, mb: 1 }}
            />
          </ListItemIcon>
          {TEST_TYPES_LABELS[TEST_TYPES.BONE_CONDUCTION]}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPinnedTestType(TEST_TYPES.BONE_CONDUCTION_MASKING);
            handleTestTypeSelection(TEST_TYPES.BONE_CONDUCTION_MASKING);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={
                LEGEND_IMAGE_MAPPING[menuState.selectedEar || "left"][
                  TEST_TYPES.BONE_CONDUCTION_MASKING
                ]
              }
              sx={{ width: 32, mb: 1 }}
            />
          </ListItemIcon>
          {TEST_TYPES_LABELS[TEST_TYPES.BONE_CONDUCTION_MASKING]}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPinnedTestType(TEST_TYPES.MCL);
            handleTestTypeSelection(TEST_TYPES.MCL);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={
                LEGEND_IMAGE_MAPPING[menuState.selectedEar || "left"][
                  TEST_TYPES.MCL
                ]
              }
              sx={{ width: 32, mb: 1 }}
            />
          </ListItemIcon>
          {TEST_TYPES_LABELS[TEST_TYPES.MCL]}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPinnedTestType(TEST_TYPES.UCL);
            handleTestTypeSelection(TEST_TYPES.UCL);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={
                LEGEND_IMAGE_MAPPING[menuState.selectedEar || "left"][
                  TEST_TYPES.UCL
                ]
              }
              sx={{ width: 32, mb: 1 }}
            />
          </ListItemIcon>
          {TEST_TYPES_LABELS[TEST_TYPES.UCL]}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPinnedTestType(TEST_TYPES.SOUNDFIELD_AIDED);
            handleTestTypeSelection(TEST_TYPES.SOUNDFIELD_AIDED);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={
                LEGEND_IMAGE_MAPPING[menuState.selectedEar || "left"][
                  TEST_TYPES.SOUNDFIELD_AIDED
                ]
              }
              sx={{ width: 32, mb: 1 }}
            />
          </ListItemIcon>
          {TEST_TYPES_LABELS[TEST_TYPES.SOUNDFIELD_AIDED]}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setPinnedTestType(TEST_TYPES.SOUNDFIELD_UNAIDED);
            handleTestTypeSelection(TEST_TYPES.SOUNDFIELD_UNAIDED);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={
                LEGEND_IMAGE_MAPPING[menuState.selectedEar || "left"][
                  TEST_TYPES.SOUNDFIELD_UNAIDED
                ]
              }
              sx={{ width: 32, mb: 1 }}
            />
          </ListItemIcon>
          {TEST_TYPES_LABELS[TEST_TYPES.SOUNDFIELD_UNAIDED]}
        </MenuItem>
      </Menu>
    </Stack>
  );
}
